import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import { api } from '../services/api';
import { HCaptcha } from '../components/HCaptcha';

interface SecurityPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const SecurityPage: React.FC<SecurityPageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');

  const handlePasswordReset = async () => {
    if (!user?.email) {
      showToast('Email utilisateur non trouvé', 'error');
      return;
    }

    if (!captchaToken) {
      showToast('Veuillez valider le captcha de sécurité', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Envoyer un magic link qui reconnecte automatiquement
      // Au lieu de resetPasswordForEmail (évite rate limiting)
      const { error } = await api.auth.signInWithOtp(user.email, captchaToken);

      if (error) {
        showToast('Erreur lors de l\'envoi de l\'email', 'error');
        return;
      }

      showToast('Magic link envoyé ! Cliquez sur le lien dans l\'email pour vous reconnecter et changer votre mot de passe.', 'success');
      
      // Optionnel : se déconnecter pour forcer l'utilisation du magic link
      setTimeout(() => {
        showToast('Déconnexion pour sécurité. Utilisez le lien dans l\'email.', 'success');
      }, 3000);
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (deleteConfirmation !== 'SUPPRIMER') {
      showToast('Veuillez taper "SUPPRIMER" pour confirmer', 'error');
      return;
    }

    if (!user) {
      showToast('Utilisateur non trouvé', 'error');
      return;
    }

    setIsDeleting(true);

    try {
      // Appeler la fonction serveur qui supprime profil + compte auth
      const { error: deleteError } = await supabase.rpc('delete_own_account');

      if (deleteError) {
        showToast('Erreur lors de la suppression du compte', 'error');
        return;
      }

      // Déconnecter l'utilisateur et rediriger
      await signOut();
      showToast('Compte supprimé avec succès. Au revoir !', 'success');
      navigate('/');
      
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeleteConfirmation('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-shield-alt text-white text-2xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Sécurité
          </h1>
          <p className="text-gray-600">
            Gérez la sécurité de votre compte
          </p>
        </div>

        {/* Section mot de passe */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            <i className="fas fa-key text-red-600 mr-2"></i>
            Changer votre mot de passe
          </h2>
          
          <div className="bg-[#002c46]/5 border border-[#003d5c]/20 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <i className="fas fa-shield-alt text-[#002c46] mr-3 mt-1"></i>
              <div>
                <h3 className="font-semibold text-[#001a2a] mb-2">Deux méthodes pour changer votre mot de passe</h3>
<div className="text-[#001a2a] text-sm space-y-2">
                  <p><strong>• Changement direct :</strong> Saisissez votre mot de passe actuel et définissez-en un nouveau immédiatement.</p>
                  <p><strong>• Magic link :</strong> Recevez un lien dans votre email pour vous reconnecter automatiquement. Une fois connecté, utilisez "Changer directement".</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-semibold text-gray-900">Email associé</h3>
                <p className="text-gray-600 text-sm">{user?.email}</p>
              </div>
              <div className="text-green-600">
                <i className="fas fa-check-circle text-xl"></i>
              </div>
            </div>

            {/* hCaptcha - Protection pour envoi email */}
            <div className="space-y-3">
              <div className={`p-4 rounded-lg border-2 transition-all ${
                captchaToken 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-300 bg-gray-50'
              }`}>
                {!captchaToken && (
                  <HCaptcha
                    onVerify={(token) => {
                      setCaptchaToken(token);
                    }}
                    onExpire={() => {
                      setCaptchaToken('');
                    }}
                    onError={() => {
                      setCaptchaToken('');
                      showToast('Erreur de vérification. Veuillez réessayer.', 'error');
                    }}
                  />
                )}
                
                {captchaToken ? (
                  <div className="text-center py-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-3">
                      <i className="fas fa-check text-white text-3xl"></i>
                    </div>
                    <p className="text-lg font-bold text-green-700 mb-1">
                      ✅ Vérification réussie !
                    </p>
                    <p className="text-sm text-green-600">
                      Vous pouvez maintenant envoyer le lien
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-600 text-center mt-2">
                    <i className="fas fa-shield-alt mr-1"></i>
                    Captcha requis pour envoyer le lien par email
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/reset-password')}
                className="py-3 px-6 bg-[#002c46] text-white rounded-lg hover:bg-[#001a2a] transition-colors font-medium shadow-lg"
              >
                <i className="fas fa-key mr-2"></i>
                Changer directement
              </button>
              
              <button
                onClick={handlePasswordReset}
                disabled={isSubmitting || !captchaToken}
                className="py-3 px-6 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                title={!captchaToken ? 'Validez le captcha pour continuer' : ''}
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Envoi en cours...
                  </>
                ) : !captchaToken ? (
                  <>
                    <i className="fas fa-lock mr-2"></i>
                    Validez le captcha
                  </>
                ) : (
                  <>
                    <i className="fas fa-envelope mr-2"></i>
                    Envoyer magic link
                  </>
                )}
              </button>
            </div>
            
            <div className="mt-4 text-sm text-gray-600 space-y-2">
              <div className="flex items-start">
                <i className="fas fa-key text-[#002c46] mr-2 mt-1 text-xs"></i>
                <p><strong>Changement direct :</strong> Accédez immédiatement au formulaire de changement de mot de passe</p>
              </div>
              <div className="flex items-start">
                <i className="fas fa-envelope text-red-600 mr-2 mt-1 text-xs"></i>
                <p><strong>Lien par email :</strong> Recevez un email avec un lien sécurisé pour changer votre mot de passe</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section informations de sécurité */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            <i className="fas fa-info text-[#002c46] mr-2"></i>
            Informations de sécurité
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-semibold text-gray-900">Authentification</h3>
                <p className="text-gray-600 text-sm">Connexion par lien magique sécurisé</p>
              </div>
              <div className="text-green-600">
                <i className="fas fa-shield-check text-xl"></i>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-semibold text-gray-900">Données personnelles</h3>
                <p className="text-gray-600 text-sm">Stockées de manière sécurisée avec Supabase</p>
              </div>
              <div className="text-green-600">
                <i className="fas fa-lock text-xl"></i>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-semibold text-gray-900">Accès au profil</h3>
                <p className="text-gray-600 text-sm">Seul vous pouvez modifier vos informations</p>
              </div>
              <div className="text-green-600">
                <i className="fas fa-user-shield text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Section suppression de profil */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6 border-l-4 border-red-500">
          <h2 className="text-xl font-bold text-red-900 mb-6">
            <i className="fas fa-exclamation-triangle text-red-600 mr-2"></i>
            Zone dangereuse
          </h2>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <i className="fas fa-warning text-red-600 mr-3 mt-1"></i>
              <div>
                <h3 className="font-semibold text-red-900 mb-2">Supprimer mon profil</h3>
                <p className="text-red-800 text-sm mb-3">
                  Cette action est <strong>irréversible</strong>. Toutes vos données seront définitivement supprimées :
                </p>
                <ul className="text-red-800 text-sm space-y-1 list-disc list-inside">
                  <li>Informations personnelles et académiques</li>
                  <li>Matières et disponibilités</li>
                  <li>Historique des matches et demandes</li>
                  <li>Évaluations reçues</li>
                  <li>Toutes les données de contact</li>
                </ul>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowDeleteModal(true)}
            className="w-full py-3 px-6 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-lg"
          >
            <i className="fas fa-trash-alt mr-2"></i>
            Supprimer définitivement mon profil
          </button>
        </div>

        {/* Modal de confirmation de suppression */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="text-center mb-6">
                <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Confirmer la suppression
                </h3>
                <p className="text-gray-600">
                  Cette action est définitive et ne peut pas être annulée.
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Pour confirmer, tapez <span className="text-red-600 font-mono">SUPPRIMER</span> :
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Tapez SUPPRIMER"
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmation('');
                  }}
                  className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteProfile}
                  disabled={isDeleting || deleteConfirmation !== 'SUPPRIMER'}
                  className="flex-1 py-3 px-6 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Suppression...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash-alt mr-2"></i>
                      Supprimer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Boutons de navigation */}
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/profile')}
            className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Retour au profil
          </button>
        </div>
      </div>
    </div>
  );
}; 